import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * When this value changes, the boundary resets and re-renders its children. Pass the current
   * route so navigating away from a crashed page clears the error without a full reload.
   */
  resetKey?: string;
  /** Compact inline fallback (keeps surrounding chrome like the nav) instead of the full-page one. */
  variant?: 'page' | 'app';
}

interface ErrorBoundaryState {
  hasError: boolean;
  message?: string;
  componentStack?: string;
}

/**
 * Catches render-time errors in the subtree and shows a recoverable fallback instead of letting a
 * single component blank the whole app (a white screen). React error boundaries must be class
 * components. The error is logged to the console so the diagnostics ring buffer (see lib/diagnostics)
 * captures it for support.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, message: error.message };
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Reset when the reset key changes (e.g. route change) so the user can navigate out of a crash.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, message: undefined });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled render error caught by ErrorBoundary:', error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? undefined });
  }

  override render() {
    if (!this.state.hasError) return this.props.children;

    const isApp = this.props.variant === 'app';
    return (
      <div
        role="alert"
        className={
          isApp
            ? 'min-h-screen flex items-center justify-center bg-background px-6'
            : 'flex items-center justify-center py-24 px-6'
        }
      >
        <div className="max-w-md text-center space-y-4">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-6 w-6 text-destructive" aria-hidden="true" />
          </div>
          <div className="space-y-1">
            <h1 className="text-lg font-semibold">Something went wrong on this page</h1>
            <p className="text-sm text-muted-foreground">
              This part of Edison hit an unexpected error and couldn&rsquo;t render. Your data is safe —
              try reloading, or head back to the dashboard.
            </p>
          </div>
          <div className="flex items-center justify-center gap-2 pt-1">
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Reload
            </button>
            <a
              href="/dashboard"
              className="inline-flex items-center rounded-lg border px-4 py-2 text-sm font-medium hover:bg-accent"
            >
              Go to dashboard
            </a>
          </div>
          {this.state.message && (
            <details className="mt-2 text-left">
              <summary className="cursor-pointer text-xs text-muted-foreground">Technical details</summary>
              <pre className="mt-2 max-h-48 overflow-auto rounded-md border bg-muted/50 p-3 text-left text-[11px] leading-relaxed whitespace-pre-wrap break-words">
                {this.state.message}
                {this.state.componentStack ? `\n${this.state.componentStack}` : ''}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
